import {Choice} from "../models/answer";
import {Question, QuestionType} from "../models/question";

/**
 * Index of the choice on a consent question that means "I accept".
 * Mirrors the API: meaning is carried by index so creators can relabel the buttons.
 */
export const AFFIRMATIVE_CHOICE_INDEX = 0;
/**
 * Index of the choice on a consent question that means "I decline".
 */
export const NEGATIVE_CHOICE_INDEX = 1;

/**
 * How close to the bottom of the consent document (in px) counts as having reached the end.
 */
export const SCROLL_GATE_TOLERANCE_PX = 24;

export type ConsentDecision = "accept" | "decline";

export interface ConsentChoices {
	affirmative: Choice;
	negative: Choice;
}

export const isConsentQuestion = (question?: Question): boolean => question?.type === QuestionType.CONSENT;

/**
 * Picks the affirmative and negative choices of a consent question by index.
 * Returns undefined if either is missing, which means the question is malformed.
 */
export const getConsentChoices = (question: Pick<Question, "choices">): ConsentChoices | undefined => {
	const affirmative = question.choices?.find(choice => choice.index === AFFIRMATIVE_CHOICE_INDEX);
	const negative = question.choices?.find(choice => choice.index === NEGATIVE_CHOICE_INDEX);
	if (!affirmative || !negative) return undefined;
	return {affirmative, negative};
};

/**
 * Whether the respondent has declined consent on this survey.
 * `hasUserConsent` is the single source of truth: false means declined,
 * true means accepted, null/undefined means unanswered or no consent question.
 */
export const hasDeclinedConsent = (survey?: {hasUserConsent?: boolean | null} | null): boolean =>
	survey?.hasUserConsent === false;

export interface ScrollMetrics {
	scrollTop: number;
	scrollHeight: number;
	clientHeight: number;
}

/**
 * The scroll gate is satisfied when the bottom of the scrollable content is within
 * `tolerance` px of the viewport, which includes content too short to scroll at all.
 */
export const isScrollGateSatisfied = (
	{scrollTop, scrollHeight, clientHeight}: ScrollMetrics,
	tolerance: number = SCROLL_GATE_TOLERANCE_PX
): boolean => {
	if (clientHeight <= 0) return false;
	return scrollHeight - scrollTop - clientHeight <= tolerance;
};
